import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
} from "react";
import useEmblaCarousel from "embla-carousel-react";
import type { EmblaOptionsType, EmblaPluginType } from "embla-carousel";

export type CarouselControlsType = "dots" | "bars";

export type CarouselContextValue = ReturnType<typeof useCarousel>;

const CarouselContext = createContext<CarouselContextValue | null>(null);

const useCarouselContext = () => {
  const context = useContext(CarouselContext);
  if (!context) {
    throw new Error(
      "Carousel compound parts must be rendered inside <Carousel.Root>",
    );
  }
  return context;
};

/**
 * Headless carousel mechanics — a thin wrapper over Embla (based on shadcn)
 * (https://www.embla-carousel.com). `opts` is Embla's own options object and
 * `plugins` is an array of plugin instances (like Autoplay), both passed
 * straight through as the same escape hatch shadcn uses.
 * **/
const useCarousel = (
  opts: EmblaOptionsType = {},
  plugins?: EmblaPluginType[],
) => {
  const [viewportRef, emblaApi] = useEmblaCarousel(opts, plugins);

  const [canScrollNext, setCanScrollNext] = useState(false);
  const [canScrollPrev, setCanScrollPrev] = useState(false);

  // Control-only state (dots/bars) — nothing else in Carousel reads these.
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [scrollSnapCount, setScrollSnapCount] = useState(1);

  useEffect(() => {
    if (!emblaApi) return;

    const onChange = () => {
      setCanScrollNext(emblaApi.canScrollNext());
      setCanScrollPrev(emblaApi.canScrollPrev());
      setSelectedIndex(emblaApi.selectedScrollSnap());
      setScrollSnapCount(emblaApi.scrollSnapList().length);
    };

    onChange();

    emblaApi.on("select", onChange).on("reInit", onChange);

    return () => {
      emblaApi.off("select", onChange).off("reInit", onChange);
    };
  }, [emblaApi]);

  const scrollNext = useCallback(() => emblaApi?.scrollNext(), [emblaApi]);
  const scrollPrev = useCallback(() => emblaApi?.scrollPrev(), [emblaApi]);
  const scrollTo = useCallback(
    (index: number) => emblaApi?.scrollTo(index),
    [emblaApi],
  );

  return {
    viewportRef,
    selectedIndex,
    scrollSnapCount,
    scrollNext,
    scrollPrev,
    scrollTo,
    canScrollNext,
    canScrollPrev,
  };
};

export { CarouselContext, useCarouselContext, useCarousel };
